import React, {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { styled } from 'styled-components';
import { checkOnScreen } from '@/hooks/observerHook';
import { emojiPattern } from '@/utils/emojiRegex';

const StyledText = styled.div`
  margin: 0;
  padding: 0;
  display: flex;
  flex-flow: column wrap;
  word-break: keep-all;
  gap: 0;
  & * {
    margin: 0;
    padding: 0;
  }
  @media (max-width: 768px) {
    white-space: pre-line;
  }
  @media not (max-width: 768px) {
    white-space: nowrap;
  }
  ${(props) => `
  .pending {
    &, & * {
      color:var(--font-color) !important;
    }
    ${props.$guide ? 'opacity: 0.1;' : 'visibility: hidden;'}
  }
  & > *:nth-child(n+${props.$line + 2}) {
    &, & * {
      color:var(--font-color) !important;
    }
    ${props.$guide ? 'opacity: 0.1;' : 'visibility: hidden;'}
  }
`}
  & div.current > * {
    display: inline;
  }
  & .typing::after {
    content: '';
    padding-right: 1px;
    border-width: 2px;
    border-right-style: solid;
    animation: cursor 0.4s infinite;
  }
  @keyframes cursor {
    0%,
    100% {
      border-color: var(--font-color);
    }
    50% {
      border-color: transparent;
    }
  }
`;

const initial = {
  textList: [],
  emojiList: [],
  line: 0,
  index: 0,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'initialState': {
      const extractText = (node) => {
        if (typeof node === 'string') {
          return node;
        }
        if (Array.isArray(node)) {
          return node.map(extractText);
        }
        if (isValidElement(node)) {
          return extractText(node.props.children);
        }
        return '';
      };
      const textList = extractText(action.children).map((el) =>
        Array.isArray(el) ? el.join('') : el
      );
      const emojiList = textList.map((el) => [
        ...el.matchAll(new RegExp(emojiPattern, 'gu')),
      ]);
      return {
        ...initial,
        textList,
        emojiList,
      };
    }
    case 'typingCurrent': {
      const char =
        state.emojiList[state.line]
          .find((el) => el.index === state.index)
          ?.at(0) ?? state.textList[state.line][state.index];
      return {
        ...state,
        index: state.index + char.length,
      };
    }
    case 'newLine':
      return {
        ...state,
        line: state.line + 1,
        index: 0,
      };
    case 'reset':
      return {
        ...state,
        line: 0,
        index: 0,
      };
    default:
      return state;
  }
};

/**
 *
 * @param {Object} props - 타이핑에 필요한 속성을 포함한 객체
 * @param {number} props.speed - 타이핑 속도 (옵션, 기본값: 100)
 * @param {boolean} props.infinity - 무한 반복 여부 (옵션, 기본값: false)
 * @param {boolean} props.guide - 타이핑 가이드 표시 여부 (옵션, 기본값: false)
 * @param {boolean} props.lineIndicator - 개행 위치에 기호 표시 여부 (옵션, 기본값: false)
 * @param {React.ReactNode} props.children - 타이핑할 텍스트 노드 (필수)
 * @param {Object} props.rest - 추가 속성 및 이벤트 핸들러 (옵션)
 * @returns {JSX.Element} 타이핑 효과가 적용된 텍스트를 렌더링하는 JSX 엘리먼트
 */
const TypingText = ({
  speed = 100,
  infinity,
  guide,
  enter,
  children,
  ...rest
}) => {
  const ref = useRef();
  const on = checkOnScreen(ref);
  const [state, dispatch] = useReducer(reducer, null);
  const RESET_DELEY = 5000;
  let interval, timeout;

  const clearTimer = () => {
    clearInterval(interval);
    clearTimeout(timeout);
  };

  const repeat = (callback, deley = null) => {
    if (!deley) {
      interval = setInterval(callback, speed);
    } else {
      timeout = setTimeout(callback, deley);
    }
  };

  useEffect(() => {
    clearTimer();
    dispatch({ type: 'initialState', children });
  }, [children]);

  useEffect(() => {
    if (state === null || !on) return;
    if (state.index < state.textList[state.line].length) {
      repeat(() => dispatch({ type: 'typingCurrent' }));
    } else if (state.line < state.textList.length - 1) {
      repeat(() => dispatch({ type: 'newLine', children }));
    } else {
      clearTimer();
      infinity && repeat(() => dispatch({ type: 'reset' }), RESET_DELEY);
    }
    return clearTimer;
  }, [children, state?.line, state?.index, on]);

  return (
    <StyledText
      ref={ref}
      className="drag-prevent"
      $line={state?.line}
      $index={state?.index}
      $guide={guide}
      $enter={enter}
      {...rest}
    >
      {state
        ? Children.map(children, (child, i) => {
            return i !== state.line ? child : Element(child, state);
          })
        : children}
    </StyledText>
  );
};

const Element = (element, state) => {
  const children = element.props.children;
  if (typeof children === 'string') {
    return (
      <div className="current">
        {cloneElement(element, {
          className: 'typing',
          children: children.slice(0, state.index),
        })}
        {cloneElement(element, {
          className: 'pending',
          children: children.slice(state.index),
        })}
      </div>
    );
  }
  const lengthList = element.props.children
    .map((child) => child.props?.children ?? child)
    .map((child) => child.length ?? 0);
  const sumLength = (num) =>
    lengthList.reduce((acc, cur, i) => (i < num ? acc + cur : acc), 0);

  return (
    <div className="current">
      {cloneElement(element, {
        children: Children.map(children, (child, i) => {
          if (sumLength(i + 1) < state.index) return child;
          const currentLength = state.index - sumLength(i);
          if (currentLength > 0) {
            return (
              <span>
                {isValidElement(child) ? (
                  cloneElement(child, {
                    className: 'typing',
                    children: child.props.children.slice(0, currentLength),
                  })
                ) : (
                  <span className="typing">
                    {child.slice(0, currentLength)}
                  </span>
                )}
                <span className="pending">
                  {(child.props?.children ?? child).slice(currentLength)}
                </span>
              </span>
            );
          }
          return <span className="pending">{child}</span>;
        }),
      })}
    </div>
  );
};

export default TypingText;
